import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Tv, Radio, ExternalLink } from "lucide-react";

export default function Live() {
  const [liveList, setLiveList] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = () => {
    api.get("/fixtures?status=live").then((r) => setLiveList(r.data));
    api.get("/fixtures?status=upcoming").then((r) => setUpcoming(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const teamById = (id) => teams.find((t) => t.id === id);
  const upcomingCast = upcoming.filter((f) => f.stream_url || (f.streamer_signups || []).length > 0);

  // Live tab must only surface matches that are actually being cast.
  const liveCast = liveList.filter((f) => f.stream_url || (f.streamer_signups || []).length > 0);
  const nothingLive = liveCast.length === 0 && upcomingCast.length === 0;

  const CastCard = ({ f, isLive }) => {
    const h = teamById(f.home_team_id);
    const a = teamById(f.away_team_id);
    const signups = f.streamer_signups || [];
    const primaryUrl = f.stream_url || signups[0]?.channel_url;
    return (
      <div
        data-testid={`${isLive ? "live" : "upcoming"}-fixture-${f.id}`}
        className={`border ${isLive ? "border-red-500/60" : "border-[#232330]"} bg-[#101015] p-5 hover:border-[#B084FF] transition-all`}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Link to={`/live/${f.id}`} className="flex items-center gap-4 flex-1 min-w-0">
            {isLive ? (
              <span className="bg-red-600 text-white text-[10px] font-mono font-bold tracking-widest px-2 py-1 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
              </span>
            ) : (
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF]/40 px-2 py-1">
                CAST · {new Date(f.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <div className="min-w-0">
              <div className="font-heading text-xl font-black uppercase tracking-tight truncate">
                {h?.name || "TBD"}
                {isLive && (
                  <span className="font-mono text-red-500 mx-2">{f.home_score ?? 0}–{f.away_score ?? 0}</span>
                )}
                {!isLive && <span className="text-gray-500 mx-2">vs</span>}
                {a?.name || "TBD"}
              </div>
              <div className="text-xs text-cyan-400 font-mono tracking-widest mt-1">
                {f.game}{f.map ? ` · ${f.map}` : ""}
                {signups.length > 0 && (
                  <span className="text-[#B084FF] ml-2">
                    · {signups.length}/2 casters {signups.map((s) => s.handle).filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
            </div>
          </Link>
          {primaryUrl ? (
            <a
              href={primaryUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`live-stream-link-${f.id}`}
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold px-4 py-2 flex items-center gap-2"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Watch{f.stream_streamer_handle ? ` · ${f.stream_streamer_handle}` : signups[0]?.handle ? ` · ${signups[0].handle}` : ""}
            </a>
          ) : (
            <Link to={`/live/${f.id}`} className="text-red-500">
              <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-10" data-testid="live-root">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <div className="text-red-500 font-mono tracking-[0.3em] text-xs">/ ON AIR</div>
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Live Streams</h1>
        <p className="text-gray-400 mt-2 text-sm">Only cast matches show up here. Nothing else — quiet cabin, all eyes on the game.</p>
      </div>

      {nothingLive ? (
        <div
          data-testid="live-empty"
          className="border border-dashed border-[#232330] p-12 text-center"
        >
          <Tv className="w-10 h-10 text-gray-600 mx-auto mb-4" />
          <div className="font-heading font-black uppercase tracking-tight text-xl text-gray-400">
            No matches being cast right now
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-2">
            The Live tab wakes up the moment a verified streamer signs on.
          </p>
        </div>
      ) : (
        <>
          {liveCast.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Radio className="w-4 h-4 text-red-500" />
                <div className="font-heading text-xl font-black uppercase tracking-tight">On Air Now</div>
              </div>
              {liveCast.map((f) => <CastCard key={f.id} f={f} isLive />)}
            </section>
          )}

          {upcomingCast.length > 0 && (
            <section className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#B084FF] font-mono">Next up · streams attached</div>
              <div className="grid gap-2">
                {upcomingCast.slice(0, 8).map((f) => <CastCard key={f.id} f={f} isLive={false} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
